import * as React from "react";
import {useState} from "react";
import {CarouselBadge, CarouselCard} from "../../../model/RegionLoginContent.model";
import {LoginContentApi} from "../../../api/LoginContentApi";

const BADGE_ROW_LABEL: Record<CarouselBadge, string> = {
    NO_BADGE: "",
    EVENT: "Event",
    INSIGHT: "Insight",
    REPORT: "Report"
};

interface AdminLoginCarouselCardsListProps {
    cards: CarouselCard[];
    editingIndex: number | null;
    editForm?: React.ReactNode;
    onReorder: (cards: CarouselCard[]) => void;
    onToggleActive: (index: number, active: boolean) => void;
    onEdit: (index: number) => void;
    onDelete: (index: number) => void;
}

export const AdminLoginCarouselCardsList: React.FunctionComponent<AdminLoginCarouselCardsListProps> = (props) => {
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dropIndex, setDropIndex] = useState<number | null>(null);

    const clearDrag = () => {
        setDragIndex(null);
        setDropIndex(null);
    };

    const handleDrop = (targetIndex: number) => {
        if (dragIndex === null || dragIndex === targetIndex) {
            clearDrag();
            return;
        }
        const reordered = [...props.cards];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        clearDrag();
        props.onReorder(reordered);
    };

    const itemClassName = (index: number) => {
        const classes = ["admin-login-content__card-item"];
        if (props.editingIndex === index) classes.push("admin-login-content__card-item--editing");
        if (dragIndex === index) classes.push("admin-login-content__card-item--dragging");
        if (dropIndex === index && dragIndex !== index) classes.push("admin-login-content__card-item--drop-target");
        return classes.join(" ");
    };

    return (
        <div className="admin-login-content__card-list">
            {props.cards.map((card, index) => (
                <React.Fragment key={`carousel-card-${index}`}>
                    <div
                        className={itemClassName(index)}
                        data-testid={`admin-login-carousel__card-row-${index}`}
                        draggable={true}
                        onDragStart={() => setDragIndex(index)}
                        onDragOver={e => {
                            e.preventDefault();
                            setDropIndex(index);
                        }}
                        onDrop={() => handleDrop(index)}
                        onDragEnd={clearDrag}>
                        <span className="admin-login-content__drag-handle"
                              data-testid={`admin-login-carousel__grip-${index}`}>
                            <i className="fa-solid fa-grip-vertical" />
                        </span>
                        <div className="admin-login-content__card-item-image">
                            {card.imageDocumentId
                                ? (
                                    <img
                                        src={LoginContentApi.carouselImageUrl(card.imageDocumentId)}
                                        alt={card.headline}/>
                                )
                                : <div className="admin-login-content__card-item-placeholder" />}
                        </div>
                        <div className="admin-login-content__card-item-info">
                            <div className="admin-login-content__card-item-title">{card.headline}</div>
                            <div className="admin-login-content__card-item-meta">
                                {card.ctaLabel} → {card.ctaUrl}
                            </div>
                        </div>
                        {card.badge !== "NO_BADGE" && (
                            <span
                                data-testid={`admin-login-carousel__badge-${index}`}
                                className={`admin-login-content__badge admin-login-content__badge--${card.badge.toLowerCase()}`}>
                                {BADGE_ROW_LABEL[card.badge]}
                            </span>
                        )}
                        <div
                            role="switch"
                            tabIndex={0}
                            aria-checked={card.active}
                            aria-label="Toggle card visibility"
                            data-testid={`admin-login-carousel__toggle-${index}`}
                            className={`admin-login-content__toggle${card.active ? " admin-login-content__toggle--on" : ""}`}
                            onClick={() => props.onToggleActive(index, !card.active)}
                            onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    props.onToggleActive(index, !card.active);
                                }
                            }} />
                        <div className="admin-login-content__card-item-actions">
                            <button
                                type="button"
                                className="admin-login-content__action-btn"
                                title="Edit"
                                data-testid={`admin-login-carousel__edit-${index}`}
                                onClick={() => props.onEdit(index)}>
                                <i className="fa-solid fa-pencil" />
                            </button>
                            <button
                                type="button"
                                className="admin-login-content__action-btn admin-login-content__action-btn--delete"
                                title="Delete"
                                data-testid={`admin-login-carousel__delete-${index}`}
                                onClick={() => props.onDelete(index)}>
                                <i className="fa-solid fa-trash" />
                            </button>
                        </div>
                    </div>
                    {props.editingIndex === index && props.editForm}
                </React.Fragment>
            ))}
        </div>
    );
};
